import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useHousehold } from '@contexts/HouseholdContext';
import { streamFlowApi } from '@lib/streamFlowApi';

/**
 * Top-of-dashboard banner for billing state.
 *
 * Visible only when actionable: trialing (countdown), past_due (urgent),
 * inactive/canceled (call-to-action). Hidden when active+paid since
 * there's nothing for the user to do.
 *
 * Owner sees a CTA button (Subscribe / Manage billing). Members see a
 * read-only message — only the owner can act on billing.
 */
export default function BillingBanner() {
  const { activeHousehold } = useHousehold();
  const [busy, setBusy] = useState(false);
  const [stripeConfigured, setStripeConfigured] = useState<boolean | null>(null);
  const [crypto, setCrypto] = useState<{ enabled: boolean; price_per_month_usd: number; months_options: number[] } | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (!activeHousehold) return;
    streamFlowApi.getBillingStatus().then((res) => {
      if (cancelled) return;
      if (res.success && res.data) {
        setStripeConfigured(res.data.stripe_configured);
      }
    });
    // Card→crypto path is optional + flag-gated server-side; only surface it
    // when the server reports it enabled.
    streamFlowApi.getCryptoStatus().then((res) => {
      if (cancelled) return;
      if (res.success && res.data?.enabled) setCrypto(res.data);
    });
    return () => { cancelled = true; };
  }, [activeHousehold?.id]);

  if (!activeHousehold) return null;

  const status = activeHousehold.subscription_status;
  const isOwner = activeHousehold.role === 'owner';
  if (status === 'active') return null;

  const onSubscribe = async () => {
    setBusy(true);
    const res = await streamFlowApi.startCheckoutSession();
    setBusy(false);
    if (res.success && res.data?.url) {
      window.location.href = res.data.url;
    } else {
      toast.error(res.error?.message ?? 'Could not start checkout');
    }
  };

  const onPortal = async () => {
    setBusy(true);
    const res = await streamFlowApi.openBillingPortal();
    setBusy(false);
    if (res.success && res.data?.url) {
      window.location.href = res.data.url;
    } else {
      toast.error(res.error?.message ?? 'Could not open billing portal');
    }
  };

  const startCrypto = async (months: number) => {
    setBusy(true);
    const res = await streamFlowApi.startCryptoCheckout(months);
    setBusy(false);
    if (res.success && res.data?.url) {
      window.location.href = res.data.url;
    } else {
      toast.error(res.error?.message ?? 'Could not start crypto checkout');
    }
  };

  let bg = 'bg-blue-50 border-blue-200 text-blue-900';
  let title = '';
  let body = '';
  let cta: { label: string; onClick: () => void } | null = null;

  if (status === 'trialing') {
    const daysLeft = activeHousehold.trial_ends_at
      ? Math.max(0, Math.ceil((new Date(activeHousehold.trial_ends_at).getTime() - Date.now()) / 86_400_000))
      : null;
    title = daysLeft != null ? `Free trial — ${daysLeft} day${daysLeft === 1 ? '' : 's'} left` : 'Free trial active';
    body = 'Your card will be charged $15/mo when the trial ends. Cancel anytime in the billing portal.';
    if (isOwner) cta = { label: 'Manage billing', onClick: onPortal };
  } else if (status === 'past_due') {
    bg = 'bg-amber-50 border-amber-200 text-amber-900';
    title = 'Payment failed';
    body = 'Streaming will be paused until the billing issue is resolved.';
    if (isOwner) cta = { label: 'Update payment method', onClick: onPortal };
  } else if (status === 'canceled') {
    bg = 'bg-rose-50 border-rose-200 text-rose-900';
    title = 'Subscription canceled';
    body = 'Resubscribe to keep streaming.';
    if (isOwner) cta = { label: 'Resubscribe', onClick: onSubscribe };
  } else {
    // inactive — start the trial
    title = 'Start your free 7-day trial';
    body = '$15/month after the trial. Cancel anytime.';
    if (isOwner) cta = { label: 'Subscribe', onClick: onSubscribe };
  }

  return (
    <>
      <div className={`mb-4 flex items-center justify-between gap-4 rounded-lg border px-4 py-3 ${bg}`}>
        <div className="min-w-0">
          <div className="text-sm font-semibold">{title}</div>
          <div className="text-xs opacity-80">{body}</div>
        </div>
        {cta && stripeConfigured !== false ? (
          <button
            type="button"
            disabled={busy}
            onClick={cta.onClick}
            className="shrink-0 rounded-md bg-zinc-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-zinc-800 disabled:opacity-50"
          >
            {busy ? '…' : cta.label}
          </button>
        ) : null}
        {!isOwner && stripeConfigured !== false && (
          <div className="shrink-0 text-xs italic opacity-75">Owner can resolve this</div>
        )}
        {stripeConfigured === false && (
          <div className="shrink-0 text-xs italic opacity-75">Billing not configured yet</div>
        )}
      </div>

      {/* Card→crypto prepay — only when enabled server-side + the owner. Pay by
          card on MoonPay's hosted page; it settles USDC to us and prepays N months. */}
      {crypto?.enabled && isOwner && (
        <div className="mb-4 -mt-2 rounded-lg border border-zinc-200 bg-zinc-50 px-4 py-3">
          <div className="text-xs font-medium text-zinc-700">
            Prefer crypto? Prepay by card — no crypto knowledge needed; it settles to us in USDC.
          </div>
          <div className="mt-2 flex flex-wrap gap-2">
            {crypto.months_options.map((m) => (
              <button
                key={m}
                type="button"
                disabled={busy}
                onClick={() => startCrypto(m)}
                className="rounded-md border border-zinc-300 bg-white px-3 py-1.5 text-xs font-medium text-zinc-800 hover:bg-zinc-100 disabled:opacity-50"
              >
                {m} mo · ${(m * crypto.price_per_month_usd).toFixed(0)}
              </button>
            ))}
          </div>
        </div>
      )}
    </>
  );
}
